import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  FaPlus,
  FaTimes,
  FaTrash,
  FaPaperclip,
  FaStar,
  FaRobot,
  FaExternalLinkAlt
} from 'react-icons/fa';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import Button from '../../util_components/button';
import CustomInput from '../../util_components/custom_input';
import CustomLoading from '../../util_components/custom_loading';
import CustomModal from '../../util_components/custom_modal';
import CustomPlayer from '../../util_components/custom_video_react';
import 'video-react/dist/video-react.css';
import AIGettingToKnowStudentBetter from '../students/know_student_better_popup/ai';
import SmsWindow from '../reports/sms_window';
import SearchableSelect from '../reminders/SearchableSelect';
import { openChatWithUserUUID, sendMessageToUserUUID } from '../../chat/chat';
import UserRole from '../../../myt_chat/chat/enums/UserRole';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api, api_with_method } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import { format_field_value } from './format_value';
import 'react-quill/dist/quill.snow.css';
import './toggle.css';

// Same rich-text toolbar as the student-profile notes composer.
const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean']
  ]
};
const QUILL_FORMATS = ['bold', 'italic', 'list', 'bullet', 'link'];

// Parse an LLM transcript timestamp ("mm:ss" or "h:mm:ss", occasionally a bare
// seconds count) into total seconds. Returns 0 when it can't be parsed (no seek).
const parse_timestamp_seconds = (ts) => {
  if (!ts) return 0;
  const parts = String(ts).trim().split(':');
  if (parts.some((p) => p === '' || Number.isNaN(Number(p)))) return 0;
  return parts.reduce((acc, p) => acc * 60 + Number(p), 0);
};

const YES_NO = [
  { value: 'Yes', label: 'Yes', color: '#22c55e' },
  { value: 'No', label: 'No', color: '#ef4444' }
];
const YES_NO_NA = [
  { value: 'Yes', label: 'Yes', color: '#22c55e' },
  { value: 'No', label: 'No', color: '#ef4444' },
  { value: 'Not Applicable', label: 'N/A', color: '#9ca3af' }
];
const STATUS_OPTS = [
  { value: 'Red', label: 'Red', color: '#ef4444' },
  { value: 'Yellow', label: 'Yellow', color: '#f59e0b' },
  { value: 'Green', label: 'Green', color: '#22c55e' }
];
const STRENGTH_OPTS = [
  { value: 'Strong', label: 'Strong', color: '#22c55e' },
  { value: 'Weak', label: 'Weak', color: '#ef4444' }
];

// Reasons offered (multi-select) when a member is not consistent with a teacher.
const CONSISTENCY_REASONS = [
  { value: 'looking_for_teacher', label: 'Looking for teacher' },
  { value: 'scheduling_issues', label: 'Scheduling issues' },
  { value: 'quality_issues', label: 'Quality issues' },
  { value: 'technical_issues', label: 'Technical issues' },
  { value: 'looking_for_gc', label: 'Looking for GC' }
];

// Related field pairs rendered together in one row (each label/value on its own
// aligned line) instead of two separate rows.
const COMBINE_GROUPS = [];

// Auto count fields rendered as a stat-badge strip inside the "Sessions"
// section (in this order) rather than as their own form rows.
const SESSION_BADGE_KEYS = [
  'pc_finished',
  'pc_finished_as_per_plan_v2',
  'cancelled_noshow_pc',
  'group_classes_finished',
  'cancelled_noshow_gc'
];

// Badge tints mirror the PC (blue) / GC (purple) session-card colors in the
// "Sessions" table so the kinds read consistently across the form.
const STAT_BADGE_TINT = {
  pc_finished: { background: '#eff6ff', borderColor: '#bfdbfe' },
  pc_finished_as_per_plan_v2: { background: '#eff6ff', borderColor: '#bfdbfe' },
  group_classes_finished: { background: '#faf5ff', borderColor: '#e9d5ff' },
  cancelled_noshow_pc: { background: '#eff6ff', borderColor: '#bfdbfe' },
  cancelled_noshow_gc: { background: '#faf5ff', borderColor: '#e9d5ff' }
};

// Specific fields rendered as full-width highlighted sections (by key, since
// they share generic shapes like yes_no/status with non-section fields).
const FULL_WIDTH_KEYS = new Set([
  'teacher_created_3m_plan',
  'teacher_student_relationship',
  'overall_status'
]);

// "Overall Status of Member" (Red / Yellow / Green) is the one answer the
// checklist can't be submitted without — it's what the list page and reporting
// key off. Saving a draft stays unrestricted.
const OVERALL_STATUS_KEY = 'overall_status';

// Small round icon button. Explicit background/border overrides the host CMS's
// global orange `button {}` rule (which otherwise renders these as orange blobs).
const ICON_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 28,
  width: 28,
  borderRadius: 9999,
  border: '1px solid #d1d5db',
  background: 'var(--surface-card)',
  cursor: 'pointer',
  padding: 0
};

// Per-checkpoint checklist form. The field list, which fields are read-only
// (blue/auto), and which are hidden (red/not-applicable) all come from the
// server schema — this component just renders widgets by shape.
class ChecklistForm extends Component {
  state = {
    loading: true,
    saving: false,
    // Resolved checklist uuid used for save/submit. Known up-front when the
    // form is opened by uuid; resolved from the response when opened by
    // (student_uuid, check_type) on the standalone form page.
    uuid: this.props.uuid || '',
    meta: {},
    fields: [],
    reply: {},
    auto_data: {},
    teacher_options: [],
    group_class_options: [],
    message_teacher_options: [],
    uploading: false,
    sending_message: false,
    sending_student_message: false,
    show_ai_know: false,
    show_all_exchanges: false,
    all_exchanges: null,
    loading_exchanges: false,
    show_session_video: false,
    session_video: null,
    session_video_uuid: null,
    loading_session_video: false,
    show_sms_window: false,
    // Set when Submit is pressed with no Overall Status picked — paints the
    // toggle red and shows the inline "required" message until one is chosen.
    overall_status_error: false
  };

  file_input_ref = React.createRef();

  // The "Overall Status of Member" section, so a failed submit can scroll it
  // into view instead of silently doing nothing.
  overall_status_ref = React.createRef();

  // video-react Player instance of the latest-1on1 replay (for chapter seeks).
  session_player_ref = React.createRef();

  componentDidMount() {
    this.load_form();
  }

  // Loads by uuid when provided, else by (student_uuid, check_type) — the
  // standalone form page keys off the latter.
  form_load_url = () => {
    if (this.props.uuid) {
      return get_api_url(`/v2/early_retention_checklist/form/${this.props.uuid}`);
    }
    return get_api_url(
      `/v2/early_retention_checklist/form_by_student/${encodeURIComponent(
        this.props.student_uuid
      )}/${encodeURIComponent(this.props.check_type)}`
    );
  };

  load_form = () => {
    this.setState({ loading: true });
    const url = this.form_load_url();
    get_api(url, true)
      .then((res) => {
        const d = res.data.data;
        const reply = d.reply || {};
        // Review Notes starts pre-filled with the checklist tag so every saved
        // note is searchable by checkpoint. Only for still-editable rows.
        if (!reply.notes_in_hridaya && d.status !== 'submitted') {
          reply.notes_in_hridaya = `<p>#RetentionCheckList - ${d.check_label || ''}</p>`;
        }
        const meta = {
          student_name: d.student_name,
          student_uuid: d.student_uuid,
          student_slug: d.student_slug,
          check_label: d.check_label,
          checkin_date: d.checkin_date,
          day_number: d.day_number,
          week_number: d.week_number,
          pause_days_added: d.pause_days_added,
          status: d.status,
          submitted_by_name: d.submitted_by_name,
          last_1on1_session_uuid: d.last_1on1_session_uuid,
          currently_paused: d.currently_paused,
          message_dispatch_method: d.message_dispatch_method,
          student_has_phone: d.student_has_phone,
          assigned_to_ops_uuid: d.assigned_to_ops_uuid,
          student_iana_timezone: d.student_iana_timezone
        };
        this.setState({
          loading: false,
          uuid: d.uuid,
          fields: d.fields || [],
          reply,
          auto_data: d.auto_data || {},
          teacher_options: d.teachers || [],
          group_class_options: d.group_classes || [],
          message_teacher_options: d.message_teachers || [],
          meta
        });
        this.props.on_meta_loaded(meta);
      })
      .catch((e) => {
        let err = 'Failed to load form';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ loading: false });
      });
  };

  // ── value setters ───────────────────────────────────────────────────────
  set_value = (key, value) =>
    this.setState((prev) => ({
      reply: { ...prev.reply, [key]: value },
      // Picking a status clears the "required" highlight immediately.
      ...(key === OVERALL_STATUS_KEY && value ? { overall_status_error: false } : null)
    }));

  set_obj_value = (key, subkey, value) =>
    this.setState((prev) => ({
      reply: { ...prev.reply, [key]: { ...(prev.reply[key] || {}), [subkey]: value } }
    }));

  // ── teacher picker list (Main Teacher Found) ──────────────────────────────
  // Value shape: { value: 'Yes'|'No', teachers: [{ uuid, name }] }
  teacher_rows = (key) => {
    const obj = this.state.reply[key] || {};
    const list = Array.isArray(obj.teachers) ? obj.teachers : [];
    return list.length ? list : [{ uuid: '', name: '' }];
  };

  set_teacher = (key, idx, uuid) => {
    const opt = this.state.teacher_options.find((o) => o.value === uuid);
    const rows = this.teacher_rows(key).map((t, i) =>
      i === idx ? { uuid, name: opt ? opt.label : '' } : t
    );
    this.set_obj_value(key, 'teachers', rows);
  };

  add_teacher = (key) =>
    this.set_obj_value(key, 'teachers', [...this.teacher_rows(key), { uuid: '', name: '' }]);

  remove_teacher = (key, idx) => {
    const rows = this.teacher_rows(key).filter((_, i) => i !== idx);
    this.set_obj_value(key, 'teachers', rows);
  };

  // ── group classes found ───────────────────────────────────────────────────
  // Value shape: { value: 'Yes'|'No'|'Not Applicable',
  //                classes: [{ value, name, teacher_name, teacher_uuid, day_of_week, time }] }
  gc_rows = (key) => {
    const obj = this.state.reply[key] || {};
    const list = Array.isArray(obj.classes) ? obj.classes : [];
    return list.length ? list : [{ value: '' }];
  };

  // Classes actually picked — stored rows minus the empty placeholder rows the
  // older row-per-dropdown UI could leave behind in saved drafts.
  gc_added = (key) => this.gc_rows(key).filter((c) => c.value);

  // Append a picked class to the list; the picker itself stays cleared (its
  // value is always '') so the next class can be chosen straight away.
  add_gc_class = (key, optionValue) => {
    if (!optionValue) return;
    const added = this.gc_added(key);
    if (added.some((c) => c.value === optionValue)) return;
    const opt = this.state.group_class_options.find((o) => o.value === optionValue);
    if (!opt) return;
    this.set_obj_value(key, 'classes', [
      ...added,
      {
        value: opt.value,
        name: opt.label,
        teacher_name: opt.teacher_name,
        teacher_uuid: opt.teacher_uuid,
        day_of_week: opt.day_of_week,
        time: opt.time
      }
    ]);
  };

  remove_gc = (key, idx) =>
    this.set_obj_value(
      key,
      'classes',
      this.gc_added(key).filter((_, i) => i !== idx)
    );

  // ── teacher relationship list ─────────────────────────────────────────────
  rel_list = (key) => (Array.isArray(this.state.reply[key]) ? this.state.reply[key] : []);

  add_rel = (key) =>
    this.set_value(key, [...this.rel_list(key), { teacher_name: '', strength: '' }]);

  update_rel = (key, idx, subkey, value) => {
    const list = this.rel_list(key).map((r, i) => (i === idx ? { ...r, [subkey]: value } : r));
    this.set_value(key, list);
  };

  // ── teacher consistency ───────────────────────────────────────────────────
  // Value shape: { consistent: 'Yes'|'No', reasons: [key...], primary: [uuid...] }
  consistency = () => this.state.reply.teacher_consistency || {};

  // The teacher/class list (+ stats) is frozen into auto_data at checkin.
  consistency_items = () =>
    Array.isArray(this.state.auto_data.consistency_items)
      ? this.state.auto_data.consistency_items
      : [];

  set_consistency = (subkey, value) => this.set_obj_value('teacher_consistency', subkey, value);

  toggle_consistency_reason = (reason) => {
    const cur = Array.isArray(this.consistency().reasons) ? this.consistency().reasons : [];
    const next = cur.includes(reason) ? cur.filter((r) => r !== reason) : [...cur, reason];
    this.set_consistency('reasons', next);
  };

  toggle_primary = (uuid) => {
    const cur = Array.isArray(this.consistency().primary) ? this.consistency().primary : [];
    const next = cur.includes(uuid) ? cur.filter((u) => u !== uuid) : [...cur, uuid];
    this.set_consistency('primary', next);
  };

  // ── message teacher ───────────────────────────────────────────────────────
  // Value shape: { teacher_uuid, message }. The message defaults to a template
  // built from the member's name + slug until the agent edits it.
  message_teacher = () => this.state.reply.message_teacher || {};

  set_message_teacher = (subkey, value) => this.set_obj_value('message_teacher', subkey, value);

  default_teacher_message = () => {
    const { student_name, student_slug } = this.state.meta;
    return `Student ${student_name || ''}(${student_slug || ''}) retention review.`;
  };

  // The effective message text: the edited value, else the default template.
  teacher_message_text = () => {
    const v = this.message_teacher();
    return v.message === undefined ? this.default_teacher_message() : v.message;
  };

  send_teacher_message = () => {
    const { teacher_uuid } = this.message_teacher();
    const message = this.teacher_message_text().trim();
    if (!teacher_uuid) {
      this.props.set_notification_variable(true, 'error', 'Select a teacher first');
      return;
    }
    if (!message) {
      this.props.set_notification_variable(true, 'error', 'Message is empty');
      return;
    }
    this.setState({ sending_message: true });
    sendMessageToUserUUID(teacher_uuid, UserRole.TEACHER, message)
      .then((ok) => {
        this.props.set_notification_variable(
          true,
          ok ? 'success' : 'error',
          ok ? 'Message sent to teacher' : 'Could not send message'
        );
        // Clear the composed text on success so it isn't left behind / re-sent;
        // undefined reverts the box to its default template (its pristine state).
        if (ok) this.set_message_teacher('message', undefined);
      })
      .catch(() => this.props.set_notification_variable(true, 'error', 'Could not send message'))
      .finally(() => this.setState({ sending_message: false }));
  };

  open_teacher_chat = () => {
    const { teacher_uuid } = this.message_teacher();
    if (!teacher_uuid) {
      this.props.set_notification_variable(true, 'error', 'Select a teacher first');
      return;
    }
    openChatWithUserUUID(teacher_uuid, UserRole.TEACHER);
  };

  // ── message student ───────────────────────────────────────────────────────
  // Value shape (per field key): { message }. Sends to / opens chat with the
  // member directly (uuid from meta).
  set_student_message = (key, value) => this.set_obj_value(key, 'message', value);

  send_student_message = (key) => {
    const { student_uuid } = this.state.meta;
    const message = ((this.state.reply[key] || {}).message || '').trim();
    if (!student_uuid) {
      this.props.set_notification_variable(true, 'error', 'No student for this checklist');
      return;
    }
    if (!message) {
      this.props.set_notification_variable(true, 'error', 'Message is empty');
      return;
    }
    this.setState({ sending_student_message: true });
    sendMessageToUserUUID(student_uuid, UserRole.STUDENT, message)
      .then((ok) => {
        this.props.set_notification_variable(
          true,
          ok ? 'success' : 'error',
          ok ? 'Message sent to student' : 'Could not send message'
        );
        // Clear the composed text on success so it isn't left behind / re-sent.
        if (ok) this.set_student_message(key, '');
      })
      .catch(() => this.props.set_notification_variable(true, 'error', 'Could not send message'))
      .finally(() => this.setState({ sending_student_message: false }));
  };

  open_student_chat = () => {
    const { student_uuid } = this.state.meta;
    if (!student_uuid) {
      this.props.set_notification_variable(true, 'error', 'No student for this checklist');
      return;
    }
    openChatWithUserUUID(student_uuid, UserRole.STUDENT);
  };

  // The relationship picker offers every teacher/class listed in the
  // "Consistent" table — {value,label} resolved from the consistency list.
  main_teacher_options = () =>
    this.consistency_items().map((it) => ({ value: it.uuid, label: it.name || it.uuid }));

  // Set the teacher on a relationship row from the type-to-filter selector,
  // storing both the uuid and resolved display name.
  set_rel_teacher = (key, idx, uuid) => {
    const opt = this.main_teacher_options().find((o) => o.value === uuid);
    const list = this.rel_list(key).map((r, i) =>
      i === idx ? { ...r, uuid, teacher_name: opt ? opt.label : '' } : r
    );
    this.set_value(key, list);
  };

  remove_rel = (key, idx) =>
    this.set_value(
      key,
      this.rel_list(key).filter((_, i) => i !== idx)
    );

  // ── attachments (stored in reply.attachments; persisted on submit) ─────────
  attachments = () =>
    Array.isArray(this.state.reply.attachments) ? this.state.reply.attachments : [];

  upload_attachment = (file) => {
    this.setState({ uploading: true });
    const url = get_api_url('/v2/early_retention_checklist/upload_attachment');
    const form = new FormData();
    form.append('student_uuid', this.state.meta.student_uuid);
    form.append('folder', 'early_retention');
    form.append('file', file);
    api_with_method('post', url, form, true, 'formdata')
      .then((res) => {
        const att = res?.data?.data;
        if (att && att.key) this.set_value('attachments', [...this.attachments(), att]);
      })
      .catch((e) => {
        const msg = e?.response?.data?.reason || 'Failed to upload attachment';
        this.props.set_notification_variable(true, 'error', msg);
      })
      .finally(() => this.setState({ uploading: false }));
  };

  remove_attachment = (idx) =>
    this.set_value(
      'attachments',
      this.attachments().filter((_, i) => i !== idx)
    );

  handle_attachment_change = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) this.upload_attachment(file);
    e.target.value = '';
  };

  // ── persistence ───────────────────────────────────────────────────────────
  save_draft = () => this.persist('/v2/early_retention_checklist/save_draft', false);

  // The Overall Status field is only required when the checkpoint's schema
  // actually includes it as a manual (non-auto, non-hidden) field.
  overall_status_required = () => {
    const field = this.state.fields.find((f) => f.key === OVERALL_STATUS_KEY);
    return !!field && !field.read_only && !this.is_hidden_field(OVERALL_STATUS_KEY);
  };

  submit = () => {
    if (this.overall_status_required() && !this.state.reply[OVERALL_STATUS_KEY]) {
      this.setState({ overall_status_error: true });
      this.props.set_notification_variable(
        true,
        'error',
        'Select an Overall Status of Member (Red / Yellow / Green) before submitting'
      );
      if (this.overall_status_ref.current) {
        this.overall_status_ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    this.persist('/v2/early_retention_checklist/submit', true);
  };

  persist = (path, is_submit) => {
    this.setState({ saving: true });
    const url = get_api_url(path);
    post_api(url, { uuid: this.state.uuid, reply: this.state.reply }, true)
      .then(() => {
        this.setState({ saving: false });
        if (is_submit) {
          this.props.set_notification_variable(true, 'success', 'Checklist submitted');
          this.props.on_submitted();
        } else {
          this.props.set_notification_variable(true, 'success', 'Draft saved');
          this.props.on_saved_draft();
        }
      })
      .catch((e) => {
        let err = is_submit ? 'Failed to submit' : 'Failed to save draft';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ saving: false });
      });
  };

  // ── field renderers ─────────────────────────────────────────────────────────
  // Segmented toggle — one click to pick, no dropdown. Used for Yes / No / N/A
  // and the Overall Status (Red / Yellow / Green). The selected option fills
  // with its colour (opt.color, falling back to brand blue); explicit inline
  // backgrounds override the host CMS's global orange `button {}` rule.
  // Interactive states (hover/focus) and the unanswered highlight live in
  // toggle.css — inline styles can't express pseudo-classes.
  render_toggle = (value, options, onChange, has_error = false) => {
    const answered = options.some((opt) => opt.value === value);
    let modifier = '';
    // A failed submit escalates the amber "unanswered" ring to a red error ring.
    if (has_error) modifier = ' erc-toggle--error';
    else if (!answered) modifier = ' erc-toggle--empty';
    return (
      <div className={`erc-toggle${modifier}`}>
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`erc-toggle-btn${selected ? ' erc-toggle-btn--selected' : ''}`}
              style={selected ? { background: opt.color || '#2563eb' } : undefined}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  };

  render_text_input = (field, type = 'text') => {
    const val = this.state.reply[field.key] ?? '';
    return (
      <CustomInput
        show_label={false}
        type={type}
        name={field.key}
        value={val}
        style={{ width: '220px' }}
        input_style={{ width: '100%' }}
        onChange={(e) => this.set_value(field.key, e.target.value)}
      />
    );
  };

  render_manual_field = (field) => {
    switch (field.shape) {
      case 'teacher_consistency':
        return this.render_teacher_consistency();
      case 'message_teacher':
        return this.render_message_teacher();
      case 'yes_no':
        return this.render_toggle(this.state.reply[field.key] || '', YES_NO, (v) =>
          this.set_value(field.key, v)
        );
      case 'yes_no_na':
        return this.render_toggle(this.state.reply[field.key] || '', YES_NO_NA, (v) =>
          this.set_value(field.key, v)
        );
      case 'status':
        return (
          <>
            {this.render_toggle(
              this.state.reply[field.key] || '',
              STATUS_OPTS,
              (v) => this.set_value(field.key, v),
              field.key === OVERALL_STATUS_KEY && this.state.overall_status_error
            )}
            {field.key === OVERALL_STATUS_KEY && this.state.overall_status_error ? (
              <div
                className="mt-2 text-sm font-medium"
                style={{ color: 'var(--state-error-text, #991b1b)' }}
              >
                Please select an overall status before submitting.
              </div>
            ) : null}
          </>
        );
      case 'count':
        return this.render_text_input(field, 'number');
      case 'date':
        return this.render_text_input(field, 'date');
      case 'text':
        return (
          // Sized to match the "Message student" textarea (560px wide).
          <div className="w-full" style={{ maxWidth: 560 }}>
            {field.key === 'notes_in_hridaya' ? this.render_attachments(false) : null}
            <div className="erc-notes-quill w-full">
              <ReactQuill
                theme="snow"
                value={this.state.reply[field.key] || ''}
                onChange={(html) => this.set_value(field.key, html)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Add notes…"
              />
            </div>
          </div>
        );
      case 'yes_no_teacher': {
        const obj = this.state.reply[field.key] || {};
        const rows = this.teacher_rows(field.key);
        return (
          <div className="flex flex-col gap-2 items-start">
            {this.render_toggle(obj.value || '', YES_NO, (v) =>
              this.set_obj_value(field.key, 'value', v)
            )}
            {rows.map((t, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="flex items-center gap-2" key={idx}>
                <div style={{ width: '240px' }}>
                  <SearchableSelect
                    value={t.uuid || ''}
                    options={this.state.teacher_options}
                    placeholder="Search teacher..."
                    clearable
                    onChange={(val) => this.set_teacher(field.key, idx, val)}
                  />
                </div>
                {idx === rows.length - 1 ? (
                  <button
                    type="button"
                    aria-label="Add another teacher"
                    onClick={() => this.add_teacher(field.key)}
                    style={ICON_BTN_STYLE}
                  >
                    <FaPlus size={11} color="#2563eb" />
                  </button>
                ) : null}
                {rows.length > 1 ? (
                  <button
                    type="button"
                    aria-label="Remove teacher"
                    onClick={() => this.remove_teacher(field.key, idx)}
                    style={ICON_BTN_STYLE}
                  >
                    <FaTimes size={11} color="#6b7280" />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        );
      }
      case 'group_classes_found': {
        const obj = this.state.reply[field.key] || {};
        const added = this.gc_added(field.key);
        return (
          <div className="flex flex-col gap-2 items-start">
            {this.render_toggle(obj.value || '', YES_NO_NA, (v) =>
              this.set_obj_value(field.key, 'value', v)
            )}
            {added.map((cls, idx) => (
              <div className="flex items-center gap-2" key={cls.value}>
                {this.render_gc_detail(cls)}
                <button
                  type="button"
                  aria-label="Remove group class"
                  onClick={() => this.remove_gc(field.key, idx)}
                  style={{
                    ...ICON_BTN_STYLE,
                    height: 22,
                    width: 22,
                    background: 'var(--state-error-bg, #fee2e2)',
                    border: '1px solid #fecaca'
                  }}
                >
                  <FaTimes size={10} color="#ef4444" />
                </button>
              </div>
            ))}
            <div style={{ width: '240px' }}>
              <SearchableSelect
                value=""
                options={this.state.group_class_options.filter(
                  (o) => !added.some((c) => c.value === o.value)
                )}
                placeholder="Search group class..."
                clearable
                onChange={(val) => this.add_gc_class(field.key, val)}
              />
            </div>
          </div>
        );
      }
      case 'teacher_relationships':
        return (
          <div className="flex flex-col gap-2">
            {this.rel_list(field.key).map((rel, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="flex gap-2 items-center" key={idx}>
                <div style={{ width: '220px' }}>
                  <SearchableSelect
                    value={rel.uuid || ''}
                    options={this.main_teacher_options()}
                    placeholder="Search teacher..."
                    clearable
                    onChange={(val) => this.set_rel_teacher(field.key, idx, val)}
                  />
                </div>
                {this.render_toggle(rel.strength || '', STRENGTH_OPTS, (v) =>
                  this.update_rel(field.key, idx, 'strength', v)
                )}
                <button
                  type="button"
                  aria-label="Remove teacher"
                  onClick={() => this.remove_rel(field.key, idx)}
                  style={ICON_BTN_STYLE}
                >
                  <FaTrash size={11} color="#ef4444" />
                </button>
              </div>
            ))}
            <div>
              <Button
                type="secondary"
                custom_style={{ padding: '2px 10px', fontSize: '12px' }}
                onClick={() => this.add_rel(field.key)}
              >
                + Add teacher
              </Button>
            </div>
          </div>
        );
      default:
        return this.render_text_input(field);
    }
  };

  // Repeats Setup: count + one line per slot, teacher name links to profile.
  render_repeats = (raw) => {
    const list = Array.isArray(raw?.repeats) ? raw.repeats : [];
    const count = raw?.count ?? list.length;
    return (
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
        <div className="font-medium">{count}</div>
        {list.map((r, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="py-0.5">
            {r.day_time} -{' '}
            {r.teacher_uuid ? (
              <Link
                to={`/teacherprofile?id=${encodeURIComponent(r.teacher_uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {r.teacher_name || r.teacher_uuid}
              </Link>
            ) : (
              r.teacher_name || '—'
            )}{' '}
            - {r.times}x
          </div>
        ))}
      </div>
    );
  };

  // "Name — Teacher (clickable) — Day Time" line for a selected group class.
  render_gc_detail = (cls) => (
    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
        {cls.name}
      </span>
      {' — '}
      {cls.teacher_uuid ? (
        <Link
          to={`/teacherprofile?id=${encodeURIComponent(cls.teacher_uuid)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {cls.teacher_name || cls.teacher_uuid}
        </Link>
      ) : (
        cls.teacher_name || '—'
      )}
      {cls.day_of_week ? ` — ${cls.day_of_week} ${cls.time || ''}` : ''}
    </div>
  );

  // Read-only view of Group Classes Found: the Yes/No/NA value + each class line.
  render_gc_readonly = (raw) => {
    const list = Array.isArray(raw?.classes) ? raw.classes : [];
    return (
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
        <div>{raw?.value || '—'}</div>
        {list.map((cls, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="py-0.5">
            {this.render_gc_detail(cls)}
          </div>
        ))}
      </div>
    );
  };

  // ── "Sessions done" overview (auto, live) ────────────────────────────────
  // Pill colours for a session's status, bucketed into finished / cancelled-or-
  // no-show / upcoming.
  // eslint-disable-next-line class-methods-use-this
  session_status_style = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('finish')) {
      return {
        background: 'var(--state-success-bg, #dcfce7)',
        color: 'var(--state-success-text, #166534)'
      };
    }
    if (s.includes('cancel') || s.includes('no show') || s.includes('replacement')) {
      return {
        background: 'var(--state-error-bg, #fee2e2)',
        color: 'var(--state-error-text, #991b1b)'
      };
    }
    return {
      background: 'var(--state-info-bg, #e0e7ff)',
      color: 'var(--state-info-text, #3730a3)'
    };
  };

  render_session_rating = (rating, review) => (
    <div className="mt-1 flex items-start gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
      <span className="inline-flex items-center" style={{ color: '#f59e0b' }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <FaStar key={n} size={10} color={n <= rating ? '#f59e0b' : '#d1d5db'} />
        ))}
      </span>
      {review ? <span className="italic">“{review}”</span> : null}
    </div>
  );

  // Teacher name, hyperlinked to the teacher profile (new tab) when a uuid is
  // present. Falls back to plain text / em dash.
  // eslint-disable-next-line class-methods-use-this
  render_teacher_link = (s) => {
    const name = s.teacher_name || '—';
    if (!s.teacher_uuid) return name;
    return (
      <Link
        to={`/teacherprofile?id=${encodeURIComponent(s.teacher_uuid)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {name}
      </Link>
    );
  };

  // The card's date/time links to the session details page — PC and GC pages
  // take the uuid under different query params.
  session_details_url = (s) =>
    s.kind === 'GC'
      ? `/groupsessiondetails?session_uuid=${encodeURIComponent(s.session_uuid)}`
      : `/sessiondetails?id=${encodeURIComponent(s.session_uuid)}`;

  render_session_card = (s, idx) => {
    const is_gc = s.kind === 'GC';
    const status_style = this.session_status_style(s.status);
    return (
      <div
        // eslint-disable-next-line react/no-array-index-key
        key={idx}
        className="rounded-md border p-2"
        // Whole-card tinting separates the session kinds at a glance: PC = blue,
        // GC = purple (matching the kind badge's darker shade of the same hue).
        style={{
          borderColor: is_gc ? '#e9d5ff' : '#bfdbfe',
          background: is_gc ? '#faf5ff' : '#eff6ff'
        }}
      >
        <div className="flex items-center justify-between gap-2">
          {s.session_uuid ? (
            <a
              href={this.session_details_url(s)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-blue-600 hover:underline"
            >
              {s.start_time || '—'}
            </a>
          ) : (
            <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
              {s.start_time || '—'}
            </span>
          )}
          <span
            className="inline-block rounded-full px-2 py-0.5 font-semibold"
            style={{ ...status_style, fontSize: 10 }}
          >
            {s.status || '—'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <span
            className="inline-block rounded px-1.5 py-0.5 font-bold"
            style={{
              fontSize: 10,
              background: is_gc ? '#f3e8ff' : '#dbeafe',
              color: is_gc ? '#6b21a8' : '#1e40af'
            }}
          >
            {s.kind}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
            {is_gc ? (
              <>
                {s.class_name || 'Group class'}
                {s.teacher_name ? <> — {this.render_teacher_link(s)}</> : null}
              </>
            ) : (
              this.render_teacher_link(s)
            )}
          </span>
          {!is_gc && s.duration ? (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {s.duration} min</span>
          ) : null}
        </div>
        {s.rating ? this.render_session_rating(s.rating, s.review) : null}
      </div>
    );
  };

  render_session_list = (title, list, view_all_url, view_all_label) => (
    <div className="flex-1" style={{ minWidth: 260 }}>
      {view_all_url ? (
        <a
          href={view_all_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block rounded border px-2 py-1 mb-1.5 text-xs font-medium text-blue-600 hover:underline"
          style={{ borderColor: 'var(--border-default)', background: 'var(--surface-card)' }}
        >
          {view_all_label} ↗
        </a>
      ) : null}
      <div
        className="text-xs font-semibold uppercase tracking-wide mb-1.5"
        style={{ color: 'var(--text-primary)' }}
      >
        {title}
      </div>
      {!list || list.length === 0 ? (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          No sessions.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">{list.map(this.render_session_card)}</div>
      )}
    </div>
  );

  // Full session-history pages in the hridaya CMS for this student.
  all_sessions_url = (which) => {
    const { student_uuid, student_name } = this.state.meta;
    return `/usersessions/${which}?id=${student_uuid}&user_type=STUDENT&name=${encodeURIComponent(student_name || '')}`;
  };

  render_sessions_overview = () => {
    // _v2 is the recomputed overview; submitted rows are frozen pre-rekey and
    // only carry the old key, so fall back to it.
    const data =
      this.state.auto_data.sessions_overview_v2 || this.state.auto_data.sessions_overview || {};
    const byKey = {};
    this.state.fields.forEach((f) => {
      byKey[f.key] = f;
    });
    const badge_fields = SESSION_BADGE_KEYS.map((k) => byKey[k]).filter(Boolean);
    return (
      <div>
        {badge_fields.length ? (
          <div className="flex flex-wrap gap-3 mb-4">
            {badge_fields.map(this.render_stat_badge)}
          </div>
        ) : null}
        <div className="flex gap-4 flex-wrap">
          {this.render_session_list(
            'Last 5 sessions',
            data.past,
            this.all_sessions_url('past'),
            'View all past sessions'
          )}
          {this.render_session_list(
            'Next 5 sessions',
            data.future,
            this.all_sessions_url('future'),
            'View all future sessions'
          )}
        </div>
      </div>
    );
  };

  // ── "Membership timeline" (auto) ─────────────────────────────────────────
  // eslint-disable-next-line class-methods-use-this
  timeline_dot_color = (type) =>
    ({
      account_created: '#9ca3af',
      membership_signup: '#22c55e',
      plan_change: '#2563eb',
      pause: '#f59e0b'
    })[type] || '#9ca3af';

  render_membership_timeline = () => {
    // Prefer the v2 key (plan/prepay split); fall back to the legacy key so
    // already-submitted rows frozen under the old shape still render.
    const events = Array.isArray(this.state.auto_data.membership_timeline_v2)
      ? this.state.auto_data.membership_timeline_v2
      : Array.isArray(this.state.auto_data.membership_timeline)
        ? this.state.auto_data.membership_timeline
        : [];
    const next_renewal = this.state.auto_data.next_renewal_date;
    // Cancelled memberships carry the same date under a distinct key: it is when
    // the membership ends, not when it renews. Only one of the two is ever set.
    const membership_end = this.state.auto_data.membership_end_date;
    const sessions_remaining =
      typeof this.state.auto_data.sessions_remaining_v2 === 'number'
        ? this.state.auto_data.sessions_remaining_v2
        : this.state.auto_data.sessions_remaining;
    const has_sessions = typeof sessions_remaining === 'number';
    if (events.length === 0 && !next_renewal && !membership_end && !has_sessions) {
      return (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          No membership events.
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {has_sessions || next_renewal || membership_end ? (
          <div className="flex flex-wrap items-center gap-2">
            {has_sessions ? (
              <div
                className="text-sm rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5"
                style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
              >
                <span style={{ color: 'var(--text-muted)' }}>Sessions remaining:</span>
                <span className="font-semibold" style={{ color: '#1d4ed8' }}>
                  {sessions_remaining}
                </span>
              </div>
            ) : null}
            {next_renewal ? (
              <div
                className="text-sm rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5"
                style={{ background: '#ecfdf5', border: '1px solid #a7f3d0' }}
              >
                <span style={{ color: 'var(--text-muted)' }}>Next renewal date:</span>
                <span className="font-semibold" style={{ color: '#047857' }}>
                  {next_renewal}
                </span>
              </div>
            ) : null}
            {membership_end ? (
              <div
                className="text-sm rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5"
                style={{
                  background: 'var(--state-warning-bg)',
                  border: '1px solid var(--state-warning)'
                }}
              >
                <span style={{ color: 'var(--text-muted)' }}>Membership ends on:</span>
                <span className="font-semibold" style={{ color: 'var(--state-warning-text)' }}>
                  {membership_end}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
        {events.map((e, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="flex gap-3 items-start">
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: 9999,
                background: this.timeline_dot_color(e.type),
                marginTop: 6,
                flexShrink: 0
              }}
            />
            <div className="text-sm">
              <div>
                {/* Three visually distinct pieces: event title, plan name,
                    prepay term. Falls back to `detail` for non-plan events. */}
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {e.title}
                </span>
                {e.plan_name ? (
                  <span className="font-semibold" style={{ color: '#7c3aed' }}>
                    {' — '}
                    {e.plan_name}
                  </span>
                ) : null}
                {e.prepay ? (
                  <span
                    className="font-semibold rounded px-1.5 py-0.5 ml-1.5"
                    style={{ color: '#9a3412', background: '#ffedd5' }}
                  >
                    {e.prepay}
                  </span>
                ) : null}
                {!e.plan_name && e.detail ? (
                  <span style={{ color: 'var(--text-muted)' }}> — {e.detail}</span>
                ) : null}
              </div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {e.date}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── "Recent chat exchanges" (auto) ───────────────────────────────────────
  // Rendered as a chat thread: student-sent bubbles align right (blue),
  // teacher-sent align left (grey). Data arrives newest-first, so reverse it to
  // read top-to-bottom like a real conversation.
  // Chat bubbles for a list of exchanges (newest-first input, rendered oldest
  // first like a chat thread). Shared by the section and the view-all modal.
  render_exchange_messages = (raw) => {
    const list = [...raw].reverse();
    return (
      <div className="flex flex-col gap-2" style={{ maxWidth: 640 }}>
        {list.map((m, idx) => {
          const mine = m.from_student; // student-sent → right side
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              className="flex"
              style={{ justifyContent: mine ? 'flex-end' : 'flex-start' }}
            >
              <div style={{ maxWidth: '78%' }}>
                <div
                  className="rounded-2xl px-3 py-2"
                  style={{
                    background: mine ? '#2563eb' : 'var(--surface-sunken, #f1f5f9)',
                    color: mine ? '#ffffff' : 'var(--text-primary)',
                    borderBottomRightRadius: mine ? 4 : 16,
                    borderBottomLeftRadius: mine ? 16 : 4
                  }}
                >
                  <div
                    className="text-sm"
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {m.message}
                  </div>
                </div>
                <div
                  className="mt-0.5 px-1"
                  style={{
                    fontSize: 10,
                    color: 'var(--text-muted)',
                    textAlign: mine ? 'right' : 'left'
                  }}
                >
                  {m.sender_name || (mine ? 'Student' : 'Teacher')} · {m.date}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Opens a session recording modal; replay URL (session.replay_url) +
  // transcript (session_transcript_summary) are fetched on first open. Used by
  // the top "Latest 1on1 session done" button (no arg → latest 1-on-1) and the
  // per-mention "session" links in "Discussed in sessions" (their session_uuid).
  open_session_video = (session_uuid, seek_seconds = 0) => {
    const uuid = session_uuid || this.state.meta.last_1on1_session_uuid;
    if (!uuid) return;
    // Switching to a different session invalidates the cached replay/transcript.
    const switching = this.state.session_video_uuid !== uuid;
    this.setState({
      show_session_video: true,
      session_video_uuid: uuid,
      ...(switching ? { session_video: null } : {})
    });
    // Seek to the mention's timestamp once the player is ready.
    const start = () => {
      if (seek_seconds > 0) this.seek_when_ready(seek_seconds);
    };
    if (!switching && this.state.session_video) {
      start(); // replay already loaded for this session
      return;
    }
    if (!switching && this.state.loading_session_video) return; // load already in flight
    this.setState({ loading_session_video: true });
    const url = get_api_url(`/v2/early_retention_checklist/session_replay/${uuid}`);
    get_api(url, true)
      .then((res) => {
        this.setState({ session_video: res.data.data || {}, loading_session_video: false });
        start();
      })
      .catch(() => {
        this.setState({ loading_session_video: false });
        this.props.set_notification_variable(true, 'error', 'Failed to load session recording');
      });
  };

  // Jump the replay to a chapter's start and play.
  seek_session_video = (secs) => {
    const player = this.session_player_ref.current;
    if (!player) return;
    player.seek(secs);
    player.play();
  };

  // Seek the freshly-opened replay to `secs` and play, once the player has
  // mounted and loaded enough metadata to honour the seek. The player mounts
  // only after the replay URL is fetched, so poll briefly (giving up after ~6s).
  seek_when_ready = (secs, attempts = 0) => {
    if (!this.state.show_session_video) return; // modal closed — abandon
    const player = this.session_player_ref.current;
    const st = player && player.getState ? player.getState().player : null;
    if (st && st.duration > 0) {
      player.seek(secs);
      player.play();
      return;
    }
    if (attempts >= 60) return;
    this.seek_timer = setTimeout(() => this.seek_when_ready(secs, attempts + 1), 100);
  };

  // Body of the latest-1on1 modal: video player on the left (70%) with the
  // chapter strip below it, transcript on the right (30%, scrollable).
  render_session_video_body = () => {
    const { loading_session_video, session_video } = this.state;
    if (loading_session_video) {
      return (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Loading…
        </div>
      );
    }
    const data = session_video || {};
    const chapters = Array.isArray(data.chapters) ? data.chapters : [];
    return (
      <div className="flex gap-3 items-stretch" style={{ height: '62vh' }}>
        <div className="flex flex-col" style={{ width: '70%', overflow: 'hidden' }}>
          {data.replay_url ? (
            // erc-replay-player (toggle.css) makes the fluid player fill this
            // flex-sized wrapper instead of its aspect-ratio height, so the
            // chapter strip below always stays visible.
            <div className="erc-replay-player" style={{ flex: 1, minHeight: 0 }}>
              <CustomPlayer video_url={data.replay_url} player_ref={this.session_player_ref} />
            </div>
          ) : (
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              No recording available for this session.
            </div>
          )}
          {data.replay_url && chapters.length > 0 ? (
            <div className="flex gap-2 mt-2 pb-1" style={{ overflowX: 'auto', flexShrink: 0 }}>
              {chapters.map((ch) => (
                <button
                  key={`${ch.start_seconds}-${ch.title}`}
                  type="button"
                  onClick={() => this.seek_session_video(ch.start_seconds)}
                  title={ch.summary || ch.title}
                  className="flex-shrink-0 rounded-md border p-1 text-xs text-left"
                  style={{
                    borderColor: 'var(--border-default)',
                    background: 'var(--surface-card)',
                    cursor: 'pointer',
                    width: 150
                  }}
                >
                  {ch.thumb ? (
                    <img
                      src={ch.thumb}
                      alt={ch.title}
                      className="rounded w-full mb-1"
                      style={{ height: 80, objectFit: 'cover' }}
                    />
                  ) : null}
                  <div className="px-1 pb-0.5">
                    <span className="font-semibold text-blue-600">{ch.start_label}</span>
                    <span className="ml-1.5" style={{ color: 'var(--text-primary)' }}>
                      {ch.title}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div
          className="rounded-md border p-2"
          style={{
            width: '30%',
            overflowY: 'auto',
            borderColor: 'var(--border-default)',
            background: 'var(--surface-sunken, #f1f5f9)'
          }}
        >
          <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>
            Transcript
          </div>
          <div
            className="text-xs"
            style={{
              color: 'var(--text-primary)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {data.transcript || 'No transcript available.'}
          </div>
        </div>
      </div>
    );
  };

  // Loads the full teacher↔student chat history (live, not frozen) on first
  // open; later opens reuse the fetched list.
  open_all_exchanges = () => {
    this.setState({ show_all_exchanges: true });
    if (this.state.all_exchanges || this.state.loading_exchanges) return;
    this.setState({ loading_exchanges: true });
    const url = get_api_url(
      `/v2/early_retention_checklist/chat_exchanges/${this.state.meta.student_uuid}`
    );
    get_api(url, true)
      .then((res) =>
        this.setState({ all_exchanges: res.data.data || [], loading_exchanges: false })
      )
      .catch(() => {
        this.setState({ loading_exchanges: false });
        this.props.set_notification_variable(true, 'error', 'Failed to load chat exchanges');
      });
  };

  render_chat_exchanges = () => {
    const raw = Array.isArray(this.state.auto_data.recent_chat_exchanges)
      ? this.state.auto_data.recent_chat_exchanges
      : [];
    if (raw.length === 0) {
      return (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          No recent chat exchanges.
        </div>
      );
    }
    return this.render_exchange_messages(raw);
  };

  // ── "Discussed in sessions" (auto) ───────────────────────────────────────
  // One row per topic. Always shows the DB-computed value; AI findings detected
  // in 1-on-1 transcripts are highlighted as amber "In session" callouts.
  render_discussion_mention = (m, idx) => (
    <div
      // eslint-disable-next-line react/no-array-index-key
      key={idx}
      className="mt-2 rounded-md p-2"
      style={{ background: '#fffbeb', border: '1px solid #fde68a' }}
    >
      <div
        className="flex items-center gap-1.5 flex-wrap"
        style={{ fontSize: 11, color: 'var(--text-muted)' }}
      >
        <span
          className="inline-block rounded px-1.5 py-0.5 font-semibold"
          style={{ fontSize: 10, background: '#fde68a', color: '#92400e' }}
        >
          In session
        </span>
        {m.teacher_name ? (
          <span>
            {m.teacher_uuid ? (
              <Link
                to={`/teacherprofile?id=${encodeURIComponent(m.teacher_uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {m.teacher_name}
              </Link>
            ) : (
              m.teacher_name
            )}
          </span>
        ) : null}
        {m.session_date ? <span>· {m.session_date}</span> : null}
        {m.timestamp ? <span>· @{m.timestamp}</span> : null}
        {m.session_uuid ? (
          <button
            type="button"
            onClick={() =>
              this.open_session_video(m.session_uuid, parse_timestamp_seconds(m.timestamp))
            }
            className="text-blue-600 hover:underline"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            · session
          </button>
        ) : null}
      </div>
      {m.reason ? (
        <div className="text-sm mt-1 italic" style={{ color: 'var(--text-primary)' }}>
          “{m.reason}”
        </div>
      ) : null}
    </div>
  );

  render_session_discussions = () => {
    // Prefer the v2 key (adds upgrade/downgrade/prepay/renewal topics); fall back
    // to the legacy key so already-submitted rows still render.
    const topics = Array.isArray(this.state.auto_data.session_discussions_v2)
      ? this.state.auto_data.session_discussions_v2
      : Array.isArray(this.state.auto_data.session_discussions)
        ? this.state.auto_data.session_discussions
        : [];
    if (topics.length === 0) {
      return (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          No session discussion data.
        </div>
      );
    }
    return (
      <div
        className="rounded-lg border overflow-hidden"
        style={{ borderColor: 'var(--border-default)', maxWidth: 760 }}
      >
        {topics.map((t, i) => {
          const mentions = Array.isArray(t.mentions) ? t.mentions : [];
          const discussed = mentions.length > 0;
          return (
            <div
              key={t.key}
              className="px-3 py-2.5"
              style={{
                borderTop: i === 0 ? 'none' : '1px solid var(--border-default)',
                background: discussed ? '#fffdf5' : 'transparent'
              }}
            >
              <div className="grid items-start gap-3" style={{ gridTemplateColumns: '160px 1fr' }}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {t.label}
                  </span>
                  {discussed ? (
                    <span
                      className="inline-block rounded-full px-1.5 py-0.5 font-semibold"
                      style={{ fontSize: 9, background: '#fde68a', color: '#92400e' }}
                    >
                      {mentions.length}
                    </span>
                  ) : null}
                </div>
                <div
                  className="text-sm"
                  style={{ color: t.data_present ? 'var(--text-primary)' : 'var(--text-muted)' }}
                >
                  {t.data_detail || '—'}
                </div>
              </div>
              {mentions.map(this.render_discussion_mention)}
            </div>
          );
        })}
      </div>
    );
  };

  // ── "Consistent" field renderers ─────────────────────────────────────────
  // Small PC/GC kind badge reused by the consistency table.
  // eslint-disable-next-line class-methods-use-this
  render_kind_badge = (kind) => (
    <span
      className="inline-block rounded px-1.5 py-0.5 font-bold mr-1.5"
      style={{
        fontSize: 10,
        background: kind === 'GC' ? '#f3e8ff' : '#dbeafe',
        color: kind === 'GC' ? '#6b21a8' : '#1e40af'
      }}
    >
      {kind}
    </span>
  );

  // Repeats cell: count + one "day time - teacher - Nx" line per slot — same
  // shape as the "Repeats Setup" field.
  // eslint-disable-next-line class-methods-use-this
  render_consistency_repeats = (repeats) => {
    const list = Array.isArray(repeats) ? repeats : [];
    if (list.length === 0) {
      return <span style={{ color: 'var(--text-muted)' }}>—</span>;
    }
    return (
      <div className="text-xs" style={{ color: 'var(--text-primary)' }}>
        <div className="font-medium">{list.length}</div>
        {list.map((r, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} style={{ color: 'var(--text-muted)' }}>
            {r.day_time}
            {r.teacher_name ? (
              <>
                {' - '}
                {r.teacher_uuid ? (
                  <Link
                    to={`/teacherprofile?id=${encodeURIComponent(r.teacher_uuid)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {r.teacher_name}
                  </Link>
                ) : (
                  r.teacher_name
                )}
              </>
            ) : null}
            {r.times ? ` - ${r.times}x` : ''}
          </div>
        ))}
      </div>
    );
  };

  // Teacher/class table: name, completed/scheduled counts, repeat slots, and a
  // primary checkbox (any number selectable).
  render_consistency_table = (primary, read_only) => {
    const items = this.consistency_items();
    if (items.length === 0) {
      return (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          No teachers or classes found for this member.
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="text-sm" style={{ minWidth: 560 }}>
          <thead>
            <tr style={{ color: 'var(--text-muted)' }}>
              <th className="text-left px-2 py-1 font-medium">Teacher / Class</th>
              <th className="text-left px-2 py-1 font-medium">Completed</th>
              <th className="text-left px-2 py-1 font-medium">Scheduled</th>
              <th className="text-left px-2 py-1 font-medium">Repeats</th>
              <th className="text-center px-2 py-1 font-medium">Primary</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const selected = primary.includes(it.uuid);
              return (
                <tr
                  key={`${it.kind}-${it.uuid}`}
                  style={{ borderTop: '1px solid var(--border-default)' }}
                >
                  <td className="px-2 py-1.5 text-left">
                    {this.render_kind_badge(it.kind)}
                    {it.kind === 'GC' ? (
                      <span style={{ color: 'var(--text-primary)' }}>
                        {it.teacher_name || '—'}
                        {it.name ? (
                          <span style={{ color: 'var(--text-muted)' }}> · {it.name}</span>
                        ) : null}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-primary)' }}>{it.name || '—'}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-left" style={{ color: 'var(--text-primary)' }}>
                    {it.completed}
                  </td>
                  <td className="px-2 py-1.5 text-left" style={{ color: 'var(--text-primary)' }}>
                    {it.scheduled}
                  </td>
                  <td className="px-2 py-1.5 align-top text-left">
                    {this.render_consistency_repeats(it.repeats)}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-center items-center">
                      {read_only ? (
                        <span style={{ color: 'var(--text-primary)' }}>
                          {selected ? 'Primary' : '—'}
                        </span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => this.toggle_primary(it.uuid)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  render_teacher_consistency = () => {
    const v = this.consistency();
    const reasons = Array.isArray(v.reasons) ? v.reasons : [];
    const primary = Array.isArray(v.primary) ? v.primary : [];
    return (
      <div className="flex flex-col gap-3">
        <div>
          {this.render_toggle(v.consistent || '', YES_NO, (val) =>
            this.set_consistency('consistent', val)
          )}
        </div>

        {v.consistent === 'No' ? (
          <div className="flex flex-col gap-1.5">
            <div className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
              Reasons
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {CONSISTENCY_REASONS.map((r) => (
                <label
                  key={r.value}
                  className="inline-flex items-center gap-1.5 text-sm cursor-pointer"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <input
                    type="checkbox"
                    checked={reasons.includes(r.value)}
                    onChange={() => this.toggle_consistency_reason(r.value)}
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
        ) : null}

        {this.render_consistency_table(primary, false)}
      </div>
    );
  };

  render_teacher_consistency_readonly = () => {
    const v = this.consistency();
    const reasons = Array.isArray(v.reasons) ? v.reasons : [];
    const primary = Array.isArray(v.primary) ? v.primary : [];
    const reason_labels = reasons.map(
      (r) => (CONSISTENCY_REASONS.find((x) => x.value === r) || {}).label || r
    );
    return (
      <div className="flex flex-col gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
        <div>
          Consistent: <span className="font-medium">{v.consistent || '—'}</span>
        </div>
        {v.consistent === 'No' && reason_labels.length ? (
          <div style={{ color: 'var(--text-muted)' }}>Reasons: {reason_labels.join(', ')}</div>
        ) : null}
        {this.render_consistency_table(primary, true)}
      </div>
    );
  };

  // ── "Message teacher" renderers ──────────────────────────────────────────
  render_message_teacher = () => {
    const v = this.message_teacher();
    const { sending_message } = this.state;
    const has_teacher = !!v.teacher_uuid;
    return (
      <div className="flex flex-col gap-2" style={{ maxWidth: 560 }}>
        <div style={{ width: 300 }}>
          <SearchableSelect
            value={v.teacher_uuid || ''}
            options={this.state.message_teacher_options}
            placeholder="Select teacher..."
            clearable
            onChange={(val) => this.set_message_teacher('teacher_uuid', val)}
          />
        </div>
        <textarea
          rows={8}
          value={this.teacher_message_text()}
          onChange={(e) => this.set_message_teacher('message', e.target.value)}
          className="w-full rounded-md border p-2 text-sm"
          style={{
            minHeight: 160,
            borderColor: 'var(--input-border)',
            background: 'var(--input-bg)',
            color: 'var(--input-text)'
          }}
        />
        <div className="flex gap-2">
          <Button disabled={sending_message || !has_teacher} onClick={this.send_teacher_message}>
            {sending_message ? 'Sending…' : 'Send chat'}
          </Button>
          <Button type="secondary" disabled={!has_teacher} onClick={this.open_teacher_chat}>
            Open chat
          </Button>
        </div>
      </div>
    );
  };

  // ── "Message student" renderer ───────────────────────────────────────────
  render_message_student = (field) => {
    const message = (this.state.reply[field.key] || {}).message || '';
    const { sending_student_message, meta } = this.state;
    const has_student = !!meta.student_uuid;
    // Member's preferred channel drives the "Send SMS / Send WhatsApp" button,
    // which opens the SMS/WhatsApp conversation window (same as student profile).
    const dispatch = meta.message_dispatch_method;
    const can_message = has_student && meta.student_has_phone && dispatch && dispatch !== 'none';
    return (
      <div className="flex flex-col gap-2" style={{ maxWidth: 560 }}>
        <textarea
          rows={6}
          value={message}
          placeholder="Message to student…"
          onChange={(e) => this.set_student_message(field.key, e.target.value)}
          className="w-full rounded-md border p-2 text-sm"
          style={{
            minHeight: 140,
            borderColor: 'var(--input-border)',
            background: 'var(--input-bg)',
            color: 'var(--input-text)'
          }}
        />
        <div className="flex gap-2">
          <Button
            disabled={sending_student_message || !has_student}
            onClick={() => this.send_student_message(field.key)}
          >
            {sending_student_message ? 'Sending…' : 'Send chat'}
          </Button>
          <Button type="secondary" disabled={!has_student} onClick={this.open_student_chat}>
            Open Chat
          </Button>
          {can_message ? (
            <Button type="secondary" onClick={() => this.setState({ show_sms_window: true })}>
              {dispatch === 'whatsapp' ? 'Send WhatsApp' : 'Send SMS'}
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  render_readonly_field = (field) => {
    const isAuto = field.read_only;
    const raw = isAuto ? this.state.auto_data[field.key] : this.state.reply[field.key];
    // Auto (blue) values are server-computed and stubbed this round.
    if (isAuto && (raw === undefined || raw === null || raw === '')) {
      return (
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Auto-populated (coming soon)
        </span>
      );
    }
    if (field.shape === 'repeats') {
      return this.render_repeats(raw);
    }
    if (field.shape === 'group_classes_found' && raw && Array.isArray(raw.classes)) {
      return this.render_gc_readonly(raw);
    }
    if (field.shape === 'text') {
      return (
        <div>
          {field.key === 'notes_in_hridaya' ? this.render_attachments(true) : null}
          {/* eslint-disable-next-line react/no-danger */}
          <div
            className="text-sm erc-notes-readonly"
            style={{ color: 'var(--text-primary)' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(raw || '') }}
          />
        </div>
      );
    }
    // "Teacher Created 3 Month Plan": the link itself signals yes — show just
    // the link when a plan exists, plain "No" otherwise.
    if (field.key === 'teacher_created_3m_plan' && raw === 'Yes' && this.state.meta.student_uuid) {
      return (
        <Link
          to={`/personalised-plan?student_uuid=${encodeURIComponent(this.state.meta.student_uuid)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline"
        >
          View 3 Month Plan
        </Link>
      );
    }
    return (
      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
        {format_field_value(field.shape, raw)}
      </span>
    );
  };

  // Membership Cancellation Date is only relevant once the student has actually
  // cancelled — hide it when auto_data has no value for it.
  is_hidden_field = (key) => {
    if (key === 'membership_cancellation_date') {
      const v = this.state.auto_data[key];
      return v === undefined || v === null || v === '';
    }
    return false;
  };

  // Collapse the configured pairs into combined rows; everything else stays a
  // single-field row. Order follows the server field list.
  build_rows = () => {
    const byKey = {};
    this.state.fields.forEach((f) => {
      byKey[f.key] = f;
    });
    const primaryOf = {};
    const merged = {};
    COMBINE_GROUPS.forEach((keys) => {
      if (keys.every((k) => byKey[k])) {
        primaryOf[keys[0]] = keys.map((k) => byKey[k]);
        keys.slice(1).forEach((k) => {
          merged[k] = true;
        });
      }
    });
    const rows = [];
    this.state.fields.forEach((f) => {
      if (merged[f.key]) return;
      // Badge fields render inside the Sessions section, not as their own rows.
      if (SESSION_BADGE_KEYS.includes(f.key)) return;
      if (this.is_hidden_field(f.key)) return;
      rows.push({ key: f.key, parts: primaryOf[f.key] || [f] });
    });
    return rows;
  };

  // One compact stat badge for the auto count strip: muted label on the left,
  // emphasised value on the right.
  render_stat_badge = (field) => {
    const raw = this.state.auto_data[field.key];
    // pc_finished is stored as {sessions} (hours not computed yet) — show the
    // bare count; everything else in the strip is a plain count/fraction.
    const value = field.shape === 'sessions_hours' ? raw && raw.sessions : raw;
    const empty = value === undefined || value === null || value === '';
    const tint = STAT_BADGE_TINT[field.key] || {
      background: 'var(--surface-sunken, #f1f5f9)',
      borderColor: 'var(--border-default)'
    };
    return (
      <div
        key={field.key}
        className="inline-flex items-center gap-2 rounded-md border px-2.5 py-1"
        style={tint}
      >
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
          {field.label}
        </span>
        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {empty ? '—' : String(value)}
        </span>
      </div>
    );
  };

  // Renders one row holding one or more fields (combined pairs stack each
  // label/value on its own aligned line).
  render_field_row = (row) => {
    const readOnly = this.props.read_only || this.state.meta.status === 'submitted';

    // Full-width sections: the sessions overview, the consistency table, and the
    // message-teacher block need the whole row width rather than the cramped
    // label/value half-split.
    const part = row.parts[0];
    const is_full_width =
      (row.parts.length === 1 &&
        [
          'membership_timeline',
          'sessions_overview',
          'teacher_consistency',
          'message_teacher',
          'message_student',
          'chat_exchanges',
          'discussion_detection',
          'group_classes_found',
          'text'
        ].includes(part.shape)) ||
      FULL_WIDTH_KEYS.has(part.key);
    if (is_full_width) {
      const show_value = part.read_only || readOnly;
      let content;
      if (part.shape === 'membership_timeline') {
        content = this.render_membership_timeline();
      } else if (part.shape === 'sessions_overview') {
        content = this.render_sessions_overview();
      } else if (part.shape === 'chat_exchanges') {
        content = this.render_chat_exchanges();
      } else if (part.shape === 'discussion_detection') {
        content = this.render_session_discussions();
      } else if (part.shape === 'teacher_consistency') {
        content = show_value
          ? this.render_teacher_consistency_readonly()
          : this.render_teacher_consistency();
      } else if (part.shape === 'message_student') {
        // Messaging / chat stays available even after the form is submitted.
        content = this.render_message_student(part);
      } else if (
        part.shape === 'text' ||
        part.shape === 'group_classes_found' ||
        FULL_WIDTH_KEYS.has(part.key)
      ) {
        // Full-width manual/auto fields rendered with their normal widget.
        content = show_value ? this.render_readonly_field(part) : this.render_manual_field(part);
      } else {
        // Messaging / chat stays available even after the form is submitted.
        content = this.render_message_teacher();
      }
      return (
        <div
          className="py-3 border-b"
          style={{ borderColor: 'var(--border-default)' }}
          key={row.key}
          ref={part.key === OVERALL_STATUS_KEY ? this.overall_status_ref : undefined}
        >
          <div
            className="flex items-center text-sm font-semibold mb-3 px-3 py-1.5 rounded-md"
            style={{
              color: 'var(--text-primary)',
              background: 'var(--surface-sunken, #f1f5f9)',
              borderLeft: '3px solid var(--brand-primary, #2563eb)'
            }}
          >
            {part.label}
            {part.key === OVERALL_STATUS_KEY && !show_value ? (
              <span
                className="ml-1"
                title="Required"
                style={{ color: 'var(--state-error, #dc2626)' }}
              >
                *
              </span>
            ) : null}
            {part.read_only ? (
              <span className="ml-2 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
                (auto)
              </span>
            ) : null}
            {part.shape === 'chat_exchanges' ? (
              <button
                type="button"
                onClick={this.open_all_exchanges}
                className="ml-3 rounded-md border px-2.5 py-0.5 text-xs font-semibold text-blue-600 hover:underline"
                style={{
                  borderColor: 'var(--border-default)',
                  background: 'var(--surface-card)',
                  cursor: 'pointer'
                }}
              >
                View all exchanges ↗
              </button>
            ) : null}
          </div>
          {content}
        </div>
      );
    }

    return (
      <div
        className="flex items-start gap-4 py-2 border-b"
        style={{ borderColor: 'var(--border-default)' }}
        key={row.key}
      >
        <div className="w-1/2 text-sm" style={{ color: 'var(--text-primary)' }}>
          {row.parts.map((field) => (
            <div key={field.key} className="py-0.5">
              {field.label}
              {field.read_only ? (
                <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                  (auto)
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <div className="w-1/2">
          {row.parts.map((field) => {
            const showValue = field.read_only || readOnly;
            return (
              <div key={field.key} className="py-0.5">
                {showValue ? this.render_readonly_field(field) : this.render_manual_field(field)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Rounded pill: muted tag label + emphasised value.
  render_pill = (label, value) => (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs"
      style={{
        background: 'var(--surface-sunken)',
        border: '1px solid var(--border-default)'
      }}
    >
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
        {value}
      </span>
    </span>
  );

  // Attachments section shown below the notes/fields.
  render_attachments = (read_only) => {
    const list = this.attachments();
    const { uploading } = this.state;
    return (
      <div className="mb-2">
        {list.length === 0 ? null : (
          <div className="flex flex-col gap-1 mb-2">
            {list.map((att, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={idx} className="flex items-center gap-2 text-sm">
                <FaPaperclip size={11} style={{ color: 'var(--text-muted)' }} />
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {att.name || att.key}
                </a>
                {!read_only ? (
                  <button
                    type="button"
                    aria-label="Remove attachment"
                    onClick={() => this.remove_attachment(idx)}
                    style={{ ...ICON_BTN_STYLE, height: 22, width: 22 }}
                  >
                    <FaTimes size={10} color="#6b7280" />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {!read_only ? (
          <>
            <input
              ref={this.file_input_ref}
              type="file"
              style={{ display: 'none' }}
              onChange={this.handle_attachment_change}
            />
            <button
              type="button"
              onClick={() => this.file_input_ref.current && this.file_input_ref.current.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border"
              style={{
                borderColor: 'var(--input-border)',
                background: 'var(--input-bg)',
                color: 'var(--text-primary)',
                cursor: uploading ? 'wait' : 'pointer'
              }}
            >
              <FaPaperclip size={11} />
              {uploading ? 'Uploading…' : 'Add attachment'}
            </button>
          </>
        ) : null}
      </div>
    );
  };

  render() {
    const { loading, saving, meta } = this.state;
    const read_only = this.props.read_only || meta.status === 'submitted';

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div>
        <div className="text-xl font-semibold mb-1">
          {meta.student_uuid ? (
            <Link
              to={`/studentprofile?id=${encodeURIComponent(meta.student_uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline capitalize"
            >
              {meta.student_name}
            </Link>
          ) : (
            <span className="capitalize" style={{ color: 'var(--text-primary)' }}>
              {meta.student_name}
            </span>
          )}
        </div>

        {/* Check label + read-only metadata pills. */}
        <div className="flex items-center flex-wrap gap-2 mb-4 text-sm">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{
              background: 'var(--state-info-bg, #e0e7ff)',
              color: 'var(--state-info-text, #3730a3)'
            }}
          >
            {meta.check_label}
          </span>
          {meta.checkin_date ? this.render_pill('Checkin Date', meta.checkin_date) : null}
          {meta.pause_days_added > 0
            ? this.render_pill('Pause Days', `+${meta.pause_days_added}`)
            : null}
          {meta.currently_paused ? (
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
              style={{
                background: 'var(--state-warning-bg, #fef3c7)',
                color: 'var(--state-warning-text, #92400e)'
              }}
            >
              Membership Paused
            </span>
          ) : null}
          {meta.submitted_by_name
            ? this.render_pill('Checklist filled by', meta.submitted_by_name)
            : null}
        </div>

        {/* Action buttons row. */}
        <div className="flex items-center flex-wrap gap-2 mb-4 text-sm">
          {meta.student_uuid ? (
            <button
              type="button"
              onClick={() => this.setState({ show_ai_know: true })}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px transition-all"
              style={{
                background: 'var(--brand-primary, #2563eb)',
                color: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                cursor: 'pointer'
              }}
            >
              <FaRobot size={12} />
              AI: Getting to know student better
            </button>
          ) : null}
          {meta.last_1on1_session_uuid ? (
            <button
              type="button"
              onClick={() => this.open_session_video()}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md hover:brightness-110 active:translate-y-px transition-all"
              style={{
                background: 'var(--brand-primary, #2563eb)',
                color: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                cursor: 'pointer'
              }}
            >
              <FaExternalLinkAlt size={11} />
              Latest 1on1 session done
            </button>
          ) : null}
        </div>

        <CustomModal
          show={this.state.show_ai_know}
          close={() => this.setState({ show_ai_know: false })}
          inner_style={{ height: '80vh', overflow: 'hidden' }}
        >
          {this.state.show_ai_know ? (
            // Scrolling lives on this wrapper (not the modal box) so the close
            // X stays put; the component's sticky title pins inside it. calc
            // leaves room for the modal's built-in close-icon row.
            <div style={{ height: 'calc(100% - 24px)', overflowY: 'auto' }}>
              <AIGettingToKnowStudentBetter data={{ student_uuid: meta.student_uuid }} />
            </div>
          ) : null}
        </CustomModal>

        <CustomModal
          show={this.state.show_all_exchanges}
          close={() => this.setState({ show_all_exchanges: false })}
          inner_style={{
            height: '75vh',
            overflow: 'hidden',
            borderRadius: 12,
            border: '1px solid var(--border-default)',
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.04)'
          }}
        >
          {this.state.show_all_exchanges ? (
            // calc leaves room for the modal's built-in close-icon row (24px)
            // above this content — without it the list bottom gets clipped.
            <div className="flex flex-col" style={{ height: 'calc(100% - 24px)' }}>
              <div
                className="text-sm font-semibold mb-3 pb-2 border-b"
                style={{ color: 'var(--text-primary)', borderColor: 'var(--border-default)' }}
              >
                All chat exchanges — {meta.student_name || 'student'} ↔ teachers
              </div>
              <div className="flex-1 pr-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {this.state.loading_exchanges ? (
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Loading…
                  </div>
                ) : null}
                {!this.state.loading_exchanges && (this.state.all_exchanges || []).length === 0 ? (
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    No chat exchanges.
                  </div>
                ) : null}
                {!this.state.loading_exchanges && (this.state.all_exchanges || []).length > 0
                  ? this.render_exchange_messages(this.state.all_exchanges)
                  : null}
              </div>
            </div>
          ) : null}
        </CustomModal>

        <CustomModal
          show={this.state.show_session_video}
          close={() => {
            clearTimeout(this.seek_timer);
            this.setState({ show_session_video: false });
          }}
          inner_style={{ width: '90%', maxWidth: 1100, borderRadius: 12 }}
        >
          {this.state.show_session_video ? (
            <div>
              <div className="flex items-center mb-3">
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {this.state.session_video_uuid === meta.last_1on1_session_uuid
                    ? 'Latest 1on1 session'
                    : 'Session recording'}
                </div>
                <a
                  href={`/sessiondetails?id=${encodeURIComponent(this.state.session_video_uuid || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-3 rounded-md border px-2.5 py-0.5 text-xs font-semibold text-blue-600 hover:underline"
                  style={{
                    borderColor: 'var(--border-default)',
                    background: 'var(--surface-sunken, #f1f5f9)'
                  }}
                >
                  Open session page ↗
                </a>
              </div>
              {this.render_session_video_body()}
            </div>
          ) : null}
        </CustomModal>

        {this.state.show_sms_window && meta.student_uuid ? (
          <SmsWindow
            student_uuid={meta.student_uuid}
            student_name={meta.student_name}
            close_fn={() => this.setState({ show_sms_window: false })}
            message_dispatch_method={meta.message_dispatch_method}
            is_allowed_sms={meta.message_dispatch_method === 'sms'}
            is_allowed_whatsapp={meta.message_dispatch_method === 'whatsapp'}
            enable_reminder
            reminder_assignee_uuid={meta.assigned_to_ops_uuid}
            student_iana_timezone={meta.student_iana_timezone}
          />
        ) : null}

        <div>{this.build_rows().map(this.render_field_row)}</div>

        {read_only ? (
          <div className="mt-6 text-sm" style={{ color: 'var(--text-muted)' }}>
            This checklist has been submitted and is read-only.
          </div>
        ) : (
          <div className="mt-6 flex justify-end gap-3">
            <Button type="secondary" disabled={saving} onClick={this.save_draft}>
              Save to draft
            </Button>
            <Button disabled={saving} onClick={this.submit}>
              {saving ? 'Saving...' : 'Submit'}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

ChecklistForm.propTypes = {
  // Either uuid OR (student_uuid + check_type) must be supplied.
  uuid: PropTypes.string,
  student_uuid: PropTypes.string,
  check_type: PropTypes.string,
  read_only: PropTypes.bool,
  on_submitted: PropTypes.func.isRequired,
  on_saved_draft: PropTypes.func.isRequired,
  // Called with the row's meta (student name/uuid/slug, check_label, …) once
  // the form data loads — lets the host page reuse it (e.g. reminder seeding).
  on_meta_loaded: PropTypes.func,
  set_notification_variable: PropTypes.func.isRequired
};

ChecklistForm.defaultProps = {
  uuid: '',
  student_uuid: '',
  check_type: '',
  read_only: false,
  on_meta_loaded: () => {}
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ChecklistForm);
